import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";

import type { SaveExactAccountPayload } from "modules/DealerDetails/components/Invoicing/components/ExactAccountModal/hooks";

export type ExactAccountFormPayload = Required<Omit<SaveExactAccountPayload, "dealer_id" | "dealer_location_id">> & {
  exact_account_code: string;
};

const FormHelper = createFormHelper<ExactAccountFormPayload>();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isExactAccountFormInvalid = (values: Omit<ExactAccountFormPayload, "exact_account_code">) =>
  Object.values(values).some(value => !value) || !EMAIL_REGEX.test(values.exact_invoicing_email);

export const ExactAccountForm = () => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const values = watch();
  const isEmailInvalid = formState.isSubmitted && !EMAIL_REGEX.test(values.exact_invoicing_email);

  return (
    <>
      <Form.Group widths="equal">
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_company_name}>
          <label>{t("v8_company_name", "Company name")}</label>
          <FormHelper.Field.Input name="exact_invoicing_company_name" />
        </Form.Field>
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_country}>
          <label>{t("v8_country", "Country")}</label>
          <FormHelper.Field.Input name="exact_invoicing_country" />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_email}>
          <label>{t("v8_invoice_email", "Invoice e-mail")}</label>
          <FormHelper.Field.Input name="exact_invoicing_email" />
          {isEmailInvalid && <div className="exact-account-field-error">{t("v8_email_address_invalid", "Invalid email address")}</div>}
        </Form.Field>
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_city}>
          <label>{t("v8_city", "City")}</label>
          <FormHelper.Field.Input name="exact_invoicing_city" />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required error={formState.isSubmitted && !values.exact_vat_number}>
          <label>{t("v8_vat_number", "VAT-Number")}</label>
          <FormHelper.Field.Input name="exact_vat_number" />
        </Form.Field>
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_address}>
          <label>{t("v8_address", "Address")}</label>
          <FormHelper.Field.Input name="exact_invoicing_address" />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required className="exact-account-recipient">
          <label>{t("v8_invoice_recipient", "Invoice recipient")}</label>
          <Form.Group>
            <Form.Field width={8} error={formState.isSubmitted && !values.exact_invoicing_initials}>
              <FormHelper.Field.Input name="exact_invoicing_initials" inputProps={{ placeholder: t("v8_initials", "Initials") }} />
            </Form.Field>
            <Form.Field width={8} error={formState.isSubmitted && !values.exact_invoicing_last_name}>
              <FormHelper.Field.Input name="exact_invoicing_last_name" inputProps={{ placeholder: t("v8_surname", "Surname") }} />
            </Form.Field>
          </Form.Group>
        </Form.Field>
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_zip_code}>
          <label>{t("v8_zip_code", "Zip code")}</label>
          <FormHelper.Field.Input name="exact_invoicing_zip_code" />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required error={formState.isSubmitted && !values.exact_chamber_of_commerce}>
          <label>{t("v8_chamber_of_commerce", "Chamber of Commerce")}</label>
          <FormHelper.Field.Input name="exact_chamber_of_commerce" />
        </Form.Field>
        <Form.Field required error={formState.isSubmitted && !values.exact_invoicing_language}>
          <label>{t("v8_language", "Language")}</label>
          <FormHelper.Field.Input name="exact_invoicing_language" />
        </Form.Field>
      </Form.Group>
      {!!values.exact_account_code && (
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("v8_account_code", "Account code")}</label>
            <FormHelper.Field.Input name="exact_account_code" inputProps={{ readOnly: true }} />
          </Form.Field>
        </Form.Group>
      )}
    </>
  );
};
